import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Table, Badge, Alert, Modal, Tabs, Tab } from "react-bootstrap";
import { FaArrowLeft, FaPlus, FaEdit, FaTrash, FaCheck, FaTimes, FaUsers, FaList, FaThLarge, FaCheckSquare } from "react-icons/fa";
import { useModulos } from "../../../services/hooks/useModulos";
import { useZonas } from "../../../services/hooks/useZonas";
import { moduloParametriaApi } from "../../../services/api/moduloParametriaApi";
import pythonApiClient from "../../../services/api/pythonBaseApi";

const MantenimientoParametriaModulos = () => {
  const navigate = useNavigate();

  const { modulos, fetchModulosByZona } = useModulos();
  const { zonas, fetchZonas } = useZonas();

  const [state, setState] = useState({
    moduloSeleccionado: null,
    zonaSeleccionada: null,
    parametria: null,
    loading: false
  });

  const [generos, setGeneros] = useState([]);
  const [regimenesCargo, setRegimenesCargo] = useState([]);
  const [showRegimenModal, setShowRegimenModal] = useState(false);
  const [regimenModalMode, setRegimenModalMode] = useState('create');
  const [regimenToEdit, setRegimenToEdit] = useState(null);
  const [regimenFormData, setRegimenFormData] = useState({
    regimen_cargo_id: '',
    activo: true
  });

  // Estado para vista masiva de regímenes
  const [vistaMasiva, setVistaMasiva] = useState({
    regimenSeleccionado: null,
    modulosSeleccionados: [],
    vistaCompleta: null,
    loading: false
  });

  // Estado para vista masiva de géneros
  const [vistaMasivaGeneros, setVistaMasivaGeneros] = useState({
    generoSeleccionado: null,
    modulosSeleccionados: [],
    vistaCompleta: null,
    loading: false
  });

  // Estado para modal de confirmación
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    details: null,
    onConfirm: null,
    variant: 'warning' // 'warning', 'danger', 'info'
  });

  // Cargar zonas, géneros y regímenes al montar
  useEffect(() => {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject'));
    if (selectedProject && selectedProject.id) {
      fetchZonas(selectedProject.id);
      loadGeneros();
      loadRegimenesCargo();
    }
  }, [fetchZonas]);

  // Cargar vista completa automáticamente cuando se carguen los regímenes
  useEffect(() => {
    if (vistaMasiva.vistaCompleta === null && regimenesCargo.length > 0) {
      loadVistaCompleta();
    }
  }, [regimenesCargo]);

  const loadGeneros = async () => {
    try {
      // Buscar endpoint de géneros - puede estar en diferentes rutas
      let response;
      try {
        response = await pythonApiClient.get('/mantenimientos/generos/');
      } catch (e1) {
        try {
          response = await pythonApiClient.get('/generos/');
        } catch (e2) {
          // Si no existe endpoint, usar datos por defecto
          setGeneros([
            { id: 1, nombre: 'Masculino' },
            { id: 2, nombre: 'Femenino' }
          ]);
          return;
        }
      }
      if (response.data && Array.isArray(response.data)) {
        setGeneros(response.data);
      } else if (response.data && response.data.generos) {
        setGeneros(response.data.generos);
      }
    } catch (error) {
      console.error('Error al cargar géneros:', error);
      // Usar datos por defecto si falla
      setGeneros([
        { id: 1, nombre: 'Masculino' },
        { id: 2, nombre: 'Femenino' }
      ]);
    }
  };

  const loadRegimenesCargo = async () => {
    try {
      const response = await pythonApiClient.get('/mantenimientos/regimenes-cargo/');
      if (response.data && Array.isArray(response.data)) {
        setRegimenesCargo(response.data);
      }
    } catch (error) {
      console.error('Error al cargar regímenes de cargo:', error);
      toast.error('Error al cargar regímenes de cargo');
    }
  };

  const handleZonaChange = async (zonaId) => {
    if (zonaId) {
      const zona = zonas.find(z => z.id === zonaId);
      setState({ ...state, zonaSeleccionada: zona, moduloSeleccionado: null, parametria: null });
      await fetchModulosByZona(zonaId);
    } else {
      setState({ ...state, zonaSeleccionada: null, moduloSeleccionado: null, parametria: null });
    }
  };

  const handleModuloSelect = async (modulo) => {
    setState({ ...state, moduloSeleccionado: modulo, loading: true });
    try {
      const response = await moduloParametriaApi.getParametriaCompleta(modulo.id);
      if (response.data) {
        setState({ ...state, moduloSeleccionado: modulo, parametria: response.data, loading: false });
      }
    } catch (error) {
      console.error('Error al cargar parametría:', error);
      toast.error('Error al cargar la parametría del módulo');
      setState({ ...state, loading: false });
    }
  };

  const handleGeneroToggle = async (generoId) => {
    if (!state.moduloSeleccionado) return;

    const generoPermitido = state.parametria?.generos_permitidos?.find(g => g.genero_id === generoId);
    
    try {
      if (generoPermitido) {
        // Remover género
        await moduloParametriaApi.removeGeneroPermitido(state.moduloSeleccionado.id, generoId);
        toast.success('Género removido correctamente');
      } else {
        // Agregar género
        await moduloParametriaApi.addGeneroPermitido(state.moduloSeleccionado.id, generoId);
        toast.success('Género agregado correctamente');
      }
      // Recargar parametría
      await handleModuloSelect(state.moduloSeleccionado);
    } catch (error) {
      console.error('Error al actualizar género:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar género');
    }
  };

  const handleOpenRegimenModal = (mode = 'create', regimen = null) => {
    setRegimenModalMode(mode);
    if (mode === 'edit' && regimen) {
      setRegimenToEdit(regimen);
      setRegimenFormData({
        regimen_cargo_id: regimen.regimen_cargo_id || '',
        activo: regimen.activo
      });
    } else {
      setRegimenToEdit(null);
      setRegimenFormData({
        regimen_cargo_id: '',
        activo: true
      });
    }
    setShowRegimenModal(true);
  };

  const handleCloseRegimenModal = () => {
    setShowRegimenModal(false);
    setRegimenToEdit(null);
    setRegimenFormData({
      regimen_cargo_id: '',
      activo: true
    });
  };

  const handleSaveRegimen = async () => {
    if (!state.moduloSeleccionado) return;
    if (!regimenFormData.regimen_cargo_id) {
      toast.error('Debe seleccionar un régimen');
      return;
    }

    try {
      if (regimenModalMode === 'create') {
        await moduloParametriaApi.createRegimenPermitido(state.moduloSeleccionado.id, {
          modulo_id: state.moduloSeleccionado.id,
          regimen_cargo_id: parseInt(regimenFormData.regimen_cargo_id),
          activo: regimenFormData.activo
        });
        toast.success('Régimen creado correctamente');
      } else {
        await moduloParametriaApi.updateRegimenPermitido(regimenToEdit.id, {
          regimen_cargo_id: parseInt(regimenFormData.regimen_cargo_id),
          activo: regimenFormData.activo
        });
        toast.success('Régimen actualizado correctamente');
      }
      handleCloseRegimenModal();
      // Recargar parametría
      await handleModuloSelect(state.moduloSeleccionado);
    } catch (error) {
      console.error('Error al guardar régimen:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar régimen');
    }
  };

  const handleDeleteRegimen = async (regimen) => {
    const regimenNombre = regimen.regimen_codigo || regimen.regimen_nombre || `ID ${regimen.regimen_cargo_id}`;
    
    setConfirmModal({
      show: true,
      title: 'Confirmar Eliminación',
      message: `¿Está seguro de eliminar el régimen "${regimenNombre}"?`,
      details: {
        tipo: 'régimen',
        nombre: regimenNombre,
        modulo: state.moduloSeleccionado?.nombre || 'N/A'
      },
      variant: 'danger',
      onConfirm: async () => {
        try {
          await moduloParametriaApi.deleteRegimenPermitido(regimen.id);
          toast.success('Régimen eliminado correctamente');
          setConfirmModal({ ...confirmModal, show: false });
          // Recargar parametría
          await handleModuloSelect(state.moduloSeleccionado);
        } catch (error) {
          console.error('Error al eliminar régimen:', error);
          toast.error(error.response?.data?.detail || 'Error al eliminar régimen');
          setConfirmModal({ ...confirmModal, show: false });
        }
      }
    });
  };

  const isGeneroPermitido = (generoId) => {
    return state.parametria?.generos_permitidos?.some(g => g.genero_id === generoId) || false;
  };

  // Funciones para vista masiva
  const loadVistaCompleta = async () => {
    setVistaMasiva(prev => ({ ...prev, loading: true }));
    try {
      const response = await pythonApiClient.get('/mantenimientos/modulos/regimenes/vista-completa');
      if (response.data) {
        setVistaMasiva(prev => ({ ...prev, vistaCompleta: response.data, loading: false }));
      }
    } catch (error) {
      console.error('Error al cargar vista completa:', error);
      toast.error('Error al cargar vista completa');
      setVistaMasiva(prev => ({ ...prev, loading: false }));
    }
  };

  const handleRegimenSelectMasivo = (regimenId) => {
    const regimen = vistaMasiva.vistaCompleta?.regimenes?.find(r => r.id === regimenId);
    if (!regimen) return;

    // Obtener módulos que ya tienen este régimen configurado
    const modulosConRegimen = [];
    if (vistaMasiva.vistaCompleta?.matriz) {
      Object.keys(vistaMasiva.vistaCompleta.matriz).forEach(moduloId => {
        const config = vistaMasiva.vistaCompleta.matriz[moduloId];
        if (config[regimenId] && config[regimenId].activo) {
          modulosConRegimen.push(parseInt(moduloId));
        }
      });
    }

    setVistaMasiva({
      ...vistaMasiva,
      regimenSeleccionado: regimen,
      modulosSeleccionados: modulosConRegimen
    });
  };

  const handleModuloToggleMasivo = (moduloId) => {
    const index = vistaMasiva.modulosSeleccionados.indexOf(moduloId);
    if (index > -1) {
      // Desmarcar
      setVistaMasiva({
        ...vistaMasiva,
        modulosSeleccionados: vistaMasiva.modulosSeleccionados.filter(id => id !== moduloId)
      });
    } else {
      // Marcar
      setVistaMasiva({
        ...vistaMasiva,
        modulosSeleccionados: [...vistaMasiva.modulosSeleccionados, moduloId]
      });
    }
  };

  const handleSelectAllModulos = () => {
    const todosLosModulos = vistaMasiva.vistaCompleta?.modulos?.map(m => m.id) || [];
    setVistaMasiva({
      ...vistaMasiva,
      modulosSeleccionados: todosLosModulos
    });
  };

  const handleDeselectAllModulos = () => {
    setVistaMasiva({
      ...vistaMasiva,
      modulosSeleccionados: []
    });
  };

  const handleAplicarConfiguracionMasiva = async () => {
    if (!vistaMasiva.regimenSeleccionado) {
      toast.error('Debe seleccionar un régimen');
      return;
    }

    if (!vistaMasiva.vistaCompleta?.modulos || vistaMasiva.vistaCompleta.modulos.length === 0) {
      toast.error('No hay módulos disponibles');
      return;
    }

    // Calcular cambios: módulos a insertar y a eliminar
    const modulosAInsertar = [];
    const modulosAEliminar = [];
    const modulosSinCambios = [];

    vistaMasiva.vistaCompleta.modulos.forEach(modulo => {
      const checkboxMarcado = vistaMasiva.modulosSeleccionados.includes(modulo.id);
      const existeEnBD = tieneRegimenConfigurado(modulo.id, vistaMasiva.regimenSeleccionado.id);
      
      if (checkboxMarcado && !existeEnBD) {
        // Checkbox marcado pero no existe en BD → INSERT
        modulosAInsertar.push(modulo);
      } else if (!checkboxMarcado && existeEnBD) {
        // Checkbox desmarcado pero existe en BD → DELETE
        modulosAEliminar.push(modulo);
      } else {
        // Sin cambios
        modulosSinCambios.push(modulo);
      }
    });

    // Preparar detalles para el modal
    const totalCambios = modulosAInsertar.length + modulosAEliminar.length;
    const modulosNombresInsertar = modulosAInsertar.slice(0, 3).map(m => `${m.codigo} - ${m.nombre}`);
    const modulosNombresEliminar = modulosAEliminar.slice(0, 3).map(m => `${m.codigo} - ${m.nombre}`);

    if (totalCambios === 0) {
      toast.info('No hay cambios para aplicar');
      return;
    }

    setConfirmModal({
      show: true,
      title: 'Confirmar Configuración Masiva',
      message: `¿Aplicar los cambios al régimen "${vistaMasiva.regimenSeleccionado.codigo}"?`,
      details: {
        tipo: 'configuración masiva',
        regimen: `${vistaMasiva.regimenSeleccionado.codigo} - ${vistaMasiva.regimenSeleccionado.nombre}`,
        insertar: modulosAInsertar.length,
        eliminar: modulosAEliminar.length,
        sinCambios: modulosSinCambios.length,
        modulosInsertar: modulosNombresInsertar,
        modulosEliminar: modulosNombresEliminar,
        totalInsertar: modulosAInsertar.length,
        totalEliminar: modulosAEliminar.length
      },
      variant: 'info',
      onConfirm: async () => {
        try {
          let exitosos = 0;
          let errores = [];

          // Procesar INSERTS
          for (const modulo of modulosAInsertar) {
            try {
              await moduloParametriaApi.createRegimenPermitido(modulo.id, {
                modulo_id: modulo.id,
                regimen_cargo_id: vistaMasiva.regimenSeleccionado.id,
                activo: true
              });
              exitosos++;
            } catch (error) {
              // Si ya existe, no es un error crítico
              if (error.response?.status === 400 && error.response?.data?.detail?.includes('ya está permitido')) {
                exitosos++; // Ya existe, consideramos éxito
              } else {
                errores.push(`Módulo ${modulo.codigo}: ${error.response?.data?.detail || 'Error al crear'}`);
              }
            }
          }

          // Procesar DELETES
          for (const modulo of modulosAEliminar) {
            try {
              // Primero intentar obtener el ID desde la matriz (más eficiente y confiable)
              const configMatriz = vistaMasiva.vistaCompleta?.matriz?.[modulo.id]?.[vistaMasiva.regimenSeleccionado.id];
              let regimenPermitidoId = null;
              
              if (configMatriz && configMatriz.id) {
                // Usar el ID de la matriz (ya viene del backend)
                regimenPermitidoId = configMatriz.id;
                console.log(`📋 Módulo ${modulo.codigo}: ID obtenido de matriz: ${regimenPermitidoId}`);
              } else {
                // Si no está en la matriz o no tiene ID, obtener de la API como fallback
                console.log(`📋 Módulo ${modulo.codigo}: Obteniendo ID desde API (fallback)...`);
                try {
                  const regimenesResponse = await moduloParametriaApi.getRegimenesPermitidos(modulo.id, false);
                  
                  if (regimenesResponse && regimenesResponse.data && Array.isArray(regimenesResponse.data)) {
                    const regimenPermitido = regimenesResponse.data.find(
                      r => r.regimen_cargo_id === vistaMasiva.regimenSeleccionado.id
                    );
                    
                    if (regimenPermitido && regimenPermitido.id) {
                      regimenPermitidoId = regimenPermitido.id;
                      console.log(`📋 Módulo ${modulo.codigo}: ID obtenido de API: ${regimenPermitidoId}`);
                    }
                  }
                } catch (apiError) {
                  console.warn(`⚠️ Error al obtener desde API para módulo ${modulo.codigo}:`, apiError);
                }
              }
              
              if (regimenPermitidoId) {
                console.log(`🗑️ Eliminando régimen ID ${regimenPermitidoId} del módulo ${modulo.codigo}`);
                await moduloParametriaApi.deleteRegimenPermitido(regimenPermitidoId);
                exitosos++;
                console.log(`✅ Régimen eliminado exitosamente del módulo ${modulo.codigo}`);
              } else {
                // Si no se encuentra el ID, verificar si realmente debería existir
                const existeEnMatriz = vistaMasiva.vistaCompleta?.matriz?.[modulo.id]?.[vistaMasiva.regimenSeleccionado.id];
                if (existeEnMatriz) {
                  // Existe en la matriz pero no tiene ID, puede ser un problema de datos
                  console.warn(`⚠️ Régimen existe en matriz pero no tiene ID para módulo ${modulo.codigo}`);
                  errores.push(`Módulo ${modulo.codigo}: No se encontró el ID del régimen para eliminar`);
                } else {
                  // No existe en la matriz, ya está eliminado o nunca existió
                  console.log(`ℹ️ Régimen ya no existe en módulo ${modulo.codigo}, considerado éxito`);
                  exitosos++;
                }
              }
            } catch (error) {
              console.error(`❌ Error al procesar eliminación para módulo ${modulo.codigo}:`, error);
              const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Error desconocido';
              errores.push(`Módulo ${modulo.codigo}: ${errorMsg}`);
              
              // Log detallado del error
              if (error.response) {
                console.error(`  Status: ${error.response.status}`);
                console.error(`  Data:`, error.response.data);
              }
            }
          }

          // Mostrar resultado
          if (errores.length > 0) {
            toast.warning(`Configuración aplicada con ${errores.length} error(es). Exitosos: ${exitosos}`);
            console.error('Errores:', errores);
          } else {
            toast.success(`Configuración aplicada correctamente. ${exitosos} cambio(s) realizado(s)`);
          }

          setConfirmModal({ ...confirmModal, show: false });
          
          // Recargar vista completa
          await loadVistaCompleta();
          
          // Mantener la selección del régimen pero recargar los checkboxes según el nuevo estado
          const regimenId = vistaMasiva.regimenSeleccionado.id;
          handleRegimenSelectMasivo(regimenId);
        } catch (error) {
          console.error('Error al aplicar configuración masiva:', error);
          toast.error(error.response?.data?.detail || 'Error al aplicar configuración masiva');
          setConfirmModal({ ...confirmModal, show: false });
        }
      }
    });
  };

  // Funciones para vista masiva de géneros
  const loadVistaCompletaGeneros = async () => {
    setVistaMasivaGeneros(prev => ({ ...prev, loading: true }));
    try {
      const response = await moduloParametriaApi.getVistaCompletaGeneros();
      if (response.data) {
        setVistaMasivaGeneros(prev => ({ ...prev, vistaCompleta: response.data, loading: false }));
      }
    } catch (error) {
      console.error('Error al cargar vista completa de géneros:', error);
      toast.error('Error al cargar vista completa de géneros');
      setVistaMasivaGeneros(prev => ({ ...prev, loading: false }));
    }
  };

  const handleGeneroSelectMasivo = (generoId) => {
    const genero = vistaMasivaGeneros.vistaCompleta?.generos?.find(g => g.id === generoId);
    if (!genero) return;

    // Obtener módulos que ya tienen este género configurado
    const modulosConGenero = [];
    if (vistaMasivaGeneros.vistaCompleta?.matriz) {
      Object.keys(vistaMasivaGeneros.vistaCompleta.matriz).forEach(moduloId => {
        const config = vistaMasivaGeneros.vistaCompleta.matriz[moduloId];
        if (config[generoId]) {
          modulosConGenero.push(parseInt(moduloId));
        }
      });
    }

    setVistaMasivaGeneros({
      ...vistaMasivaGeneros,
      generoSeleccionado: genero,
      modulosSeleccionados: modulosConGenero
    });
  };

  const handleModuloToggleMasivoGeneros = (moduloId) => {
    const index = vistaMasivaGeneros.modulosSeleccionados.indexOf(moduloId);
    if (index > -1) {
      setVistaMasivaGeneros({
        ...vistaMasivaGeneros,
        modulosSeleccionados: vistaMasivaGeneros.modulosSeleccionados.filter(id => id !== moduloId)
      });
    } else {
      setVistaMasivaGeneros({
        ...vistaMasivaGeneros,
        modulosSeleccionados: [...vistaMasivaGeneros.modulosSeleccionados, moduloId]
      });
    }
  };

  const handleSelectAllModulosGeneros = () => {
    const todosLosModulos = vistaMasivaGeneros.vistaCompleta?.modulos?.map(m => m.id) || [];
    setVistaMasivaGeneros({
      ...vistaMasivaGeneros,
      modulosSeleccionados: todosLosModulos
    });
  };

  const handleDeselectAllModulosGeneros = () => {
    setVistaMasivaGeneros({
      ...vistaMasivaGeneros,
      modulosSeleccionados: []
    });
  };

  const tieneGeneroConfigurado = (moduloId, generoId) => {
    return vistaMasivaGeneros.vistaCompleta?.matriz?.[moduloId]?.[generoId] !== undefined;
  };

  const isModuloSeleccionadoGeneros = (moduloId) => {
    return vistaMasivaGeneros.modulosSeleccionados.includes(moduloId);
  };

  const handleAplicarConfiguracionMasivaGeneros = async () => {
    if (!vistaMasivaGeneros.generoSeleccionado) {
      toast.error('Debe seleccionar un género');
      return;
    }

    if (!vistaMasivaGeneros.vistaCompleta?.modulos || vistaMasivaGeneros.vistaCompleta.modulos.length === 0) {
      toast.error('No hay módulos disponibles');
      return;
    }

    // Calcular cambios: módulos a insertar y a eliminar
    const modulosAInsertar = [];
    const modulosAEliminar = [];
    const modulosSinCambios = [];

    vistaMasivaGeneros.vistaCompleta.modulos.forEach(modulo => {
      const checkboxMarcado = vistaMasivaGeneros.modulosSeleccionados.includes(modulo.id);
      const existeEnBD = tieneGeneroConfigurado(modulo.id, vistaMasivaGeneros.generoSeleccionado.id);
      
      if (checkboxMarcado && !existeEnBD) {
        modulosAInsertar.push(modulo);
      } else if (!checkboxMarcado && existeEnBD) {
        modulosAEliminar.push(modulo);
      } else {
        modulosSinCambios.push(modulo);
      }
    });

    const totalCambios = modulosAInsertar.length + modulosAEliminar.length;
    const modulosNombresInsertar = modulosAInsertar.slice(0, 3).map(m => `${m.codigo} - ${m.nombre}`);
    const modulosNombresEliminar = modulosAEliminar.slice(0, 3).map(m => `${m.codigo} - ${m.nombre}`);

    if (totalCambios === 0) {
      toast.info('No hay cambios para aplicar');
      return;
    }

    setConfirmModal({
      show: true,
      title: 'Confirmar Configuración Masiva de Géneros',
      message: `¿Aplicar los cambios al género "${vistaMasivaGeneros.generoSeleccionado.nombre}"?`,
      details: {
        tipo: 'configuración masiva de géneros',
        genero: vistaMasivaGeneros.generoSeleccionado.nombre,
        insertar: modulosAInsertar.length,
        eliminar: modulosAEliminar.length,
        sinCambios: modulosSinCambios.length,
        modulosInsertar: modulosNombresInsertar,
        modulosEliminar: modulosNombresEliminar,
        totalInsertar: modulosAInsertar.length,
        totalEliminar: modulosAEliminar.length
      },
      variant: 'info',
      onConfirm: async () => {
        try {
          let exitosos = 0;
          let errores = [];

          // Procesar INSERTS
          for (const modulo of modulosAInsertar) {
            try {
              await moduloParametriaApi.addGeneroPermitido(modulo.id, vistaMasivaGeneros.generoSeleccionado.id);
              exitosos++;
            } catch (error) {
              errores.push(`Módulo ${modulo.codigo}: ${error.response?.data?.detail || 'Error al agregar'}`);
            }
          }

          // Procesar DELETES
          for (const modulo of modulosAEliminar) {
            try {
              await moduloParametriaApi.removeGeneroPermitido(modulo.id, vistaMasivaGeneros.generoSeleccionado.id);
              exitosos++;
            } catch (error) {
              errores.push(`Módulo ${modulo.codigo}: ${error.response?.data?.detail || 'Error al eliminar'}`);
            }
          }

          // Mostrar resultado
          if (errores.length > 0) {
            toast.warning(`Configuración aplicada con ${errores.length} error(es). Exitosos: ${exitosos}`);
            console.error('Errores:', errores);
          } else {
            toast.success(`Configuración aplicada correctamente. ${exitosos} cambio(s) realizado(s)`);
          }

          setConfirmModal({ ...confirmModal, show: false });
          
          // Recargar vista completa
          await loadVistaCompletaGeneros();
          
          // Mantener la selección del género pero recargar los checkboxes según el nuevo estado
          const generoId = vistaMasivaGeneros.generoSeleccionado.id;
          handleGeneroSelectMasivo(generoId);
        } catch (error) {
          console.error('Error al aplicar configuración masiva de géneros:', error);
          toast.error(error.response?.data?.detail || 'Error al aplicar configuración masiva de géneros');
          setConfirmModal({ ...confirmModal, show: false });
        }
      }
    });
  };

  const handleCloseConfirmModal = () => {
    setConfirmModal({ ...confirmModal, show: false, onConfirm: null });
  };

  const isModuloSeleccionado = (moduloId) => {
    return vistaMasiva.modulosSeleccionados.includes(moduloId);
  };

  const tieneRegimenConfigurado = (moduloId, regimenId) => {
    return vistaMasiva.vistaCompleta?.matriz?.[moduloId]?.[regimenId]?.activo || false;
  };

  return (
    <>
      <style>
        {`
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
            padding: 2rem 0;
          }
          
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
            margin-bottom: 2rem;
          }
          
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.5rem 2rem;
            border: none;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            font-weight: 700;
          }
          
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            border-radius: 10px;
            padding: 0.6rem 1.5rem;
            font-weight: 600;
            color: white;
            transition: all 0.3s;
          }
          
          .btn-dh-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border: none;
            border-radius: 10px;
            padding: 0.6rem 1.5rem;
            font-weight: 600;
            color: white;
          }
          
          .btn-dh-danger {
            background: linear-gradient(135deg, #eb3349 0%, #f45c43 100%);
            border: none;
            border-radius: 10px;
            padding: 0.5rem 1rem;
            color: white;
          }
          
          .genero-item {
            padding: 0.75rem 1rem;
            margin: 0.5rem 0;
            border-radius: 10px;
            border: 2px solid #e9ecef;
            cursor: pointer;
            transition: all 0.3s;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          
          .genero-item:hover {
            border-color: #667eea;
            background: #f8f9fa;
          }
          
          .genero-item.active {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            color: white;
            border-color: #11998e;
          }
          
          .regimen-badge {
            padding: 0.5rem 1rem;
            border-radius: 8px;
            font-weight: 600;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
          }
          
          .regimen-badge.active {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            color: white;
          }
          
          .regimen-badge.inactive {
            background: #e9ecef;
            color: #6c757d;
          }
        `}
      </style>

      <div className="dh-maintenance-bg">
        <div className="dh-main-header">
          <Container>
            <Row className="align-items-center">
              <Col>
                <Button
                  variant="link"
                  onClick={() => navigate(-1)}
                  className="text-white p-0 mb-2"
                  style={{ textDecoration: 'none' }}
                >
                  <FaArrowLeft /> Volver
                </Button>
                <h1 className="dh-main-title">Parametría de Módulos</h1>
                <p className="dh-main-subtitle">Configurar géneros y regímenes permitidos por módulo</p>
              </Col>
            </Row>
          </Container>
        </div>

        <Container>
          <Tabs defaultActiveKey="individual" className="mb-4">
            <Tab eventKey="individual" title={
              <span><FaList className="me-2" />Configuración Individual</span>
            }>
              <Row className="mt-3">
                <Col md={4}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header">
                  <FaList className="me-2" />
                  Seleccionar Módulo
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Zona</Form.Label>
                    <Form.Select
                      value={state.zonaSeleccionada?.id || ''}
                      onChange={(e) => handleZonaChange(parseInt(e.target.value))}
                    >
                      <option value="">Seleccione una zona</option>
                      {zonas.map((zona) => (
                        <option key={zona.id} value={zona.id}>
                          {zona.nombre}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Módulo</Form.Label>
                    <Form.Select
                      value={state.moduloSeleccionado?.id || ''}
                      onChange={(e) => {
                        const modulo = modulos.find(m => m.id === parseInt(e.target.value));
                        if (modulo) handleModuloSelect(modulo);
                      }}
                      disabled={!state.zonaSeleccionada}
                    >
                      <option value="">Seleccione un módulo</option>
                      {modulos.map((modulo) => (
                        <option key={modulo.id} value={modulo.id}>
                          {modulo.codigo} - {modulo.nombre}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  {state.moduloSeleccionado && (
                    <Alert variant="info" className="mt-3">
                      <strong>Módulo seleccionado:</strong><br />
                      {state.moduloSeleccionado.codigo} - {state.moduloSeleccionado.nombre}
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col md={8}>
              {state.loading && (
                <Card className="dh-card">
                  <Card.Body className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {!state.loading && state.moduloSeleccionado && state.parametria && (
                <>
                  {/* Géneros Permitidos */}
                  <Card className="dh-card mb-3">
                    <Card.Header className="dh-card-header">
                      <FaUsers className="me-2" />
                      Géneros Permitidos
                    </Card.Header>
                    <Card.Body>
                      <p className="text-muted mb-3">
                        Seleccione los géneros que pueden ser asignados a este módulo. 
                        Haga clic en un género para activarlo o desactivarlo.
                      </p>
                      {generos.map((genero) => {
                        const isActive = isGeneroPermitido(genero.id);
                        return (
                          <div
                            key={genero.id}
                            className={`genero-item ${isActive ? 'active' : ''}`}
                            onClick={() => handleGeneroToggle(genero.id)}
                          >
                            <span>{genero.nombre}</span>
                            {isActive ? (
                              <FaCheck className="text-white" />
                            ) : (
                              <FaTimes className="text-muted" />
                            )}
                          </div>
                        );
                      })}
                    </Card.Body>
                  </Card>

                  {/* Regímenes Permitidos */}
                  <Card className="dh-card">
                    <Card.Header className="dh-card-header d-flex justify-content-between align-items-center">
                      <span>
                        <FaList className="me-2" />
                        Regímenes Permitidos
                      </span>
                      <Button
                        variant="light"
                        size="sm"
                        onClick={() => handleOpenRegimenModal('create')}
                        className="text-white"
                        style={{ background: 'rgba(255,255,255,0.2)' }}
                      >
                        <FaPlus className="me-1" />
                        Agregar Régimen
                      </Button>
                    </Card.Header>
                    <Card.Body>
                      <p className="text-muted mb-3">
                        Configure los regímenes permitidos para este módulo. Seleccione de la lista de regímenes disponibles.
                      </p>
                      {state.parametria.regimenes_permitidos && state.parametria.regimenes_permitidos.length > 0 ? (
                        <Table responsive>
                          <thead>
                            <tr>
                              <th>Régimen</th>
                              <th>Estado</th>
                              <th>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {state.parametria.regimenes_permitidos.map((regimen) => (
                              <tr key={regimen.id}>
                                <td>
                                  <strong>{regimen.regimen_codigo || regimen.regimen_nombre || `ID ${regimen.regimen_cargo_id}`}</strong>
                                  {regimen.regimen_nombre && regimen.regimen_codigo && (
                                    <small className="text-muted d-block">{regimen.regimen_nombre}</small>
                                  )}
                                </td>
                                <td>
                                  <Badge className={regimen.activo ? 'regimen-badge active' : 'regimen-badge inactive'}>
                                    {regimen.activo ? (
                                      <>
                                        <FaCheck /> Activo
                                      </>
                                    ) : (
                                      <>
                                        <FaTimes /> Inactivo
                                      </>
                                    )}
                                  </Badge>
                                </td>
                                <td>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => handleOpenRegimenModal('edit', regimen)}
                                    className="text-primary p-0 me-2"
                                  >
                                    <FaEdit />
                                  </Button>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => handleDeleteRegimen(regimen)}
                                    className="text-danger p-0"
                                  >
                                    <FaTrash />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      ) : (
                        <Alert variant="info">
                          No hay regímenes configurados. Haga clic en "Agregar Régimen" para crear uno.
                        </Alert>
                      )}
                    </Card.Body>
                  </Card>
                </>
              )}

              {!state.loading && !state.moduloSeleccionado && (
                <Card className="dh-card">
                  <Card.Body className="text-center text-muted">
                    <FaList size={48} className="mb-3" />
                    <p>Seleccione un módulo para configurar su parametría</p>
                  </Card.Body>
                </Card>
              )}
                </Col>
              </Row>
            </Tab>

            <Tab 
              eventKey="masiva" 
              title={<span><FaThLarge className="me-2" />Configuración Masiva</span>}
              onEnter={() => {
                // Cargar vista completa cuando se entra a la pestaña
                if (vistaMasiva.vistaCompleta === null && regimenesCargo.length > 0) {
                  loadVistaCompleta();
                }
              }}
            >
              <Row className="mt-3">
                <Col md={12}>
                  <Card className="dh-card">
                    <Card.Header className="dh-card-header">
                      <FaThLarge className="me-2" />
                      Configuración Masiva de Regímenes
                    </Card.Header>
                    <Card.Body>
                      <Alert variant="info" className="mb-4">
                        <strong>Instrucciones:</strong> Seleccione un régimen y luego marque los módulos donde desea aplicarlo. 
                        Puede seleccionar múltiples módulos a la vez para aplicar la misma configuración.
                      </Alert>

                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-4">
                            <Form.Label><strong>1. Seleccione un Régimen</strong></Form.Label>
                            <Form.Select
                              value={vistaMasiva.regimenSeleccionado?.id || ''}
                              onChange={(e) => {
                                if (e.target.value) {
                                  handleRegimenSelectMasivo(parseInt(e.target.value));
                                } else {
                                  setVistaMasiva({ ...vistaMasiva, regimenSeleccionado: null, modulosSeleccionados: [] });
                                }
                              }}
                            >
                              <option value="">Seleccione un régimen</option>
                              {regimenesCargo.map((regimen) => (
                                <option key={regimen.id} value={regimen.id}>
                                  {regimen.codigo} - {regimen.nombre}
                                </option>
                              ))}
                            </Form.Select>
                            {vistaMasiva.regimenSeleccionado && (
                              <Alert variant="success" className="mt-2">
                                Régimen seleccionado: <strong>{vistaMasiva.regimenSeleccionado.codigo}</strong>
                              </Alert>
                            )}
                          </Form.Group>
                        </Col>
                        <Col md={8}>
                          {vistaMasiva.regimenSeleccionado && (
                            <div className="mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label><strong>2. Seleccione los Módulos</strong></Form.Label>
                                <div>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleSelectAllModulos}
                                    className="me-2"
                                  >
                                    <FaCheckSquare className="me-1" />
                                    Seleccionar Todos
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={handleDeselectAllModulos}
                                  >
                                    Desmarcar Todos
                                  </Button>
                                </div>
                              </div>
                              <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '8px', padding: '1rem' }}>
                                {vistaMasiva.vistaCompleta?.modulos?.map((modulo) => {
                                  const tieneRegimen = tieneRegimenConfigurado(modulo.id, vistaMasiva.regimenSeleccionado.id);
                                  const estaSeleccionado = isModuloSeleccionado(modulo.id);
                                  return (
                                    <div
                                      key={modulo.id}
                                      className="mb-2 p-2"
                                      style={{
                                        border: estaSeleccionado ? '2px solid #11998e' : '1px solid #dee2e6',
                                        borderRadius: '8px',
                                        backgroundColor: estaSeleccionado ? '#e8f5e9' : 'white',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                      }}
                                      onClick={() => handleModuloToggleMasivo(modulo.id)}
                                    >
                                      <Form.Check
                                        type="checkbox"
                                        checked={estaSeleccionado}
                                        onChange={() => handleModuloToggleMasivo(modulo.id)}
                                        label={
                                          <span>
                                            <strong>{modulo.codigo}</strong> - {modulo.nombre}
                                            {tieneRegimen && (
                                              <Badge bg="success" className="ms-2">Ya configurado</Badge>
                                            )}
                                          </span>
                                        }
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="mt-3">
                                <strong>Módulos seleccionados: {vistaMasiva.modulosSeleccionados.length}</strong>
                              </div>
                            </div>
                          )}
                        </Col>
                      </Row>

                      {vistaMasiva.regimenSeleccionado && (
                        <div className="mt-4 text-center">
                          <Button
                            variant="primary"
                            size="lg"
                            onClick={handleAplicarConfiguracionMasiva}
                            className="btn-dh-primary"
                          >
                            <FaCheck className="me-2" />
                            Guardar Configuración
                          </Button>
                          <p className="text-muted small mt-2">
                            Se sincronizarán todos los módulos según el estado de los checkboxes
                          </p>
                        </div>
                      )}

                      {!vistaMasiva.vistaCompleta && (
                        <div className="text-center mt-4">
                          <Button
                            variant="outline-primary"
                            onClick={loadVistaCompleta}
                            disabled={vistaMasiva.loading}
                          >
                            {vistaMasiva.loading ? 'Cargando...' : 'Cargar Vista Completa'}
                          </Button>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>

            <Tab eventKey="masiva-generos" title={
              <span><FaUsers className="me-2" />Configuración Masiva de Géneros</span>
            }>
              <Row className="mt-3">
                <Col md={12}>
                  <Card className="dh-card">
                    <Card.Header className="dh-card-header">
                      <FaUsers className="me-2" />
                      Configuración Masiva de Géneros
                    </Card.Header>
                    <Card.Body>
                      <Alert variant="info" className="mb-4">
                        <strong>Instrucciones:</strong> Seleccione un género y luego marque los módulos donde desea aplicarlo. 
                        Puede seleccionar múltiples módulos a la vez para aplicar la misma configuración.
                      </Alert>

                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-4">
                            <Form.Label><strong>1. Seleccione un Género</strong></Form.Label>
                            <Form.Select
                              value={vistaMasivaGeneros.generoSeleccionado?.id || ''}
                              onChange={(e) => {
                                if (e.target.value) {
                                  handleGeneroSelectMasivo(parseInt(e.target.value));
                                } else {
                                  setVistaMasivaGeneros({ ...vistaMasivaGeneros, generoSeleccionado: null, modulosSeleccionados: [] });
                                }
                              }}
                            >
                              <option value="">Seleccione un género</option>
                              {generos.map((genero) => (
                                <option key={genero.id} value={genero.id}>
                                  {genero.nombre}
                                </option>
                              ))}
                            </Form.Select>
                            {vistaMasivaGeneros.generoSeleccionado && (
                              <Alert variant="success" className="mt-2">
                                Género seleccionado: <strong>{vistaMasivaGeneros.generoSeleccionado.nombre}</strong>
                              </Alert>
                            )}
                          </Form.Group>
                        </Col>
                        <Col md={8}>
                          {vistaMasivaGeneros.generoSeleccionado && (
                            <div className="mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label><strong>2. Seleccione los Módulos</strong></Form.Label>
                                <div>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleSelectAllModulosGeneros}
                                    className="me-2"
                                  >
                                    <FaCheckSquare className="me-1" />
                                    Seleccionar Todos
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={handleDeselectAllModulosGeneros}
                                  >
                                    Desmarcar Todos
                                  </Button>
                                </div>
                              </div>
                              <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '8px', padding: '1rem' }}>
                                {vistaMasivaGeneros.vistaCompleta?.modulos?.map((modulo) => {
                                  const tieneGenero = tieneGeneroConfigurado(modulo.id, vistaMasivaGeneros.generoSeleccionado.id);
                                  const estaSeleccionado = isModuloSeleccionadoGeneros(modulo.id);
                                  return (
                                    <div
                                      key={modulo.id}
                                      className="mb-2 p-2"
                                      style={{
                                        border: estaSeleccionado ? '2px solid #11998e' : '1px solid #dee2e6',
                                        borderRadius: '8px',
                                        backgroundColor: estaSeleccionado ? '#e8f5e9' : 'white',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                      }}
                                      onClick={() => handleModuloToggleMasivoGeneros(modulo.id)}
                                    >
                                      <Form.Check
                                        type="checkbox"
                                        checked={estaSeleccionado}
                                        onChange={() => handleModuloToggleMasivoGeneros(modulo.id)}
                                        label={
                                          <span>
                                            <strong>{modulo.codigo}</strong> - {modulo.nombre}
                                            {tieneGenero && (
                                              <Badge bg="success" className="ms-2">Ya configurado</Badge>
                                            )}
                                          </span>
                                        }
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="mt-3">
                                <strong>Módulos seleccionados: {vistaMasivaGeneros.modulosSeleccionados.length}</strong>
                              </div>
                            </div>
                          )}
                        </Col>
                      </Row>

                      {vistaMasivaGeneros.generoSeleccionado && (
                        <div className="mt-4 text-center">
                          <Button
                            variant="primary"
                            size="lg"
                            onClick={handleAplicarConfiguracionMasivaGeneros}
                            className="btn-dh-primary"
                          >
                            <FaCheck className="me-2" />
                            Guardar Configuración
                          </Button>
                          <p className="text-muted small mt-2">
                            Se sincronizarán todos los módulos según el estado de los checkboxes
                          </p>
                        </div>
                      )}

                      {!vistaMasivaGeneros.vistaCompleta && (
                        <div className="text-center mt-4">
                          <Button
                            variant="outline-primary"
                            onClick={loadVistaCompletaGeneros}
                            disabled={vistaMasivaGeneros.loading}
                          >
                            {vistaMasivaGeneros.loading ? 'Cargando...' : 'Cargar Vista Completa'}
                          </Button>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>
          </Tabs>
        </Container>

        {/* Modal para Régimen */}
        <Modal show={showRegimenModal} onHide={handleCloseRegimenModal}>
          <Modal.Header closeButton>
            <Modal.Title>
              {regimenModalMode === 'create' ? 'Agregar Régimen' : 'Editar Régimen'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Régimen *</Form.Label>
                <Form.Select
                  value={regimenFormData.regimen_cargo_id}
                  onChange={(e) => setRegimenFormData({ ...regimenFormData, regimen_cargo_id: e.target.value })}
                  required
                >
                  <option value="">Seleccione un régimen</option>
                  {regimenesCargo.map((regimen) => (
                    <option key={regimen.id} value={regimen.id}>
                      {regimen.codigo} - {regimen.nombre}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Seleccione el régimen de la lista maestra
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Activo"
                  checked={regimenFormData.activo}
                  onChange={(e) => setRegimenFormData({ ...regimenFormData, activo: e.target.checked })}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseRegimenModal}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSaveRegimen}>
              {regimenModalMode === 'create' ? 'Crear' : 'Actualizar'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de Confirmación Personalizado */}
        <Modal 
          show={confirmModal.show} 
          onHide={handleCloseConfirmModal}
          centered
          size="lg"
        >
          <Modal.Header 
            closeButton
            className={`border-0 ${
              confirmModal.variant === 'danger' ? 'bg-danger text-white' :
              confirmModal.variant === 'warning' ? 'bg-warning text-dark' :
              'bg-info text-white'
            }`}
          >
            <Modal.Title className="d-flex align-items-center">
              {confirmModal.variant === 'danger' && <FaTrash className="me-2" />}
              {confirmModal.variant === 'warning' && <FaTimes className="me-2" />}
              {confirmModal.variant === 'info' && <FaCheck className="me-2" />}
              {confirmModal.title}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <div className="text-center mb-3">
              <div className={`mb-3 ${
                confirmModal.variant === 'danger' ? 'text-danger' :
                confirmModal.variant === 'warning' ? 'text-warning' :
                'text-info'
              }`}>
                {confirmModal.variant === 'danger' && <FaTrash size={48} />}
                {confirmModal.variant === 'warning' && <FaTimes size={48} />}
                {confirmModal.variant === 'info' && <FaCheck size={48} />}
              </div>
              <h5 className="mb-3">{confirmModal.message}</h5>
            </div>

            {confirmModal.details && (
              <div className="bg-light p-3 rounded mb-3">
                <h6 className="mb-2"><strong>Detalles:</strong></h6>
                <ul className="list-unstyled mb-0">
                  {confirmModal.details.regimen && (
                    <li className="mb-2">
                      <strong>Régimen:</strong> {confirmModal.details.regimen}
                    </li>
                  )}
                  {confirmModal.details.nombre && (
                    <li className="mb-2">
                      <strong>Nombre:</strong> {confirmModal.details.nombre}
                    </li>
                  )}
                  {confirmModal.details.modulo && (
                    <li className="mb-2">
                      <strong>Módulo:</strong> {confirmModal.details.modulo}
                    </li>
                  )}
                  {confirmModal.details.insertar !== undefined && (
                    <li className="mb-2">
                      <strong className="text-success">Módulos a agregar:</strong> {confirmModal.details.insertar}
                      {confirmModal.details.modulosInsertar && confirmModal.details.modulosInsertar.length > 0 && (
                        <ul className="mt-1 mb-0">
                          {confirmModal.details.modulosInsertar.map((modulo, idx) => (
                            <li key={idx} className="text-muted small">{modulo}</li>
                          ))}
                          {confirmModal.details.totalInsertar > confirmModal.details.modulosInsertar.length && (
                            <li className="text-muted small">
                              <em>... y {confirmModal.details.totalInsertar - confirmModal.details.modulosInsertar.length} más</em>
                            </li>
                          )}
                        </ul>
                      )}
                    </li>
                  )}
                  {confirmModal.details.eliminar !== undefined && (
                    <li className="mb-2">
                      <strong className="text-danger">Módulos a eliminar:</strong> {confirmModal.details.eliminar}
                      {confirmModal.details.modulosEliminar && confirmModal.details.modulosEliminar.length > 0 && (
                        <ul className="mt-1 mb-0">
                          {confirmModal.details.modulosEliminar.map((modulo, idx) => (
                            <li key={idx} className="text-muted small">{modulo}</li>
                          ))}
                          {confirmModal.details.totalEliminar > confirmModal.details.modulosEliminar.length && (
                            <li className="text-muted small">
                              <em>... y {confirmModal.details.totalEliminar - confirmModal.details.modulosEliminar.length} más</em>
                            </li>
                          )}
                        </ul>
                      )}
                    </li>
                  )}
                  {confirmModal.details.sinCambios !== undefined && confirmModal.details.sinCambios > 0 && (
                    <li className="mb-2">
                      <strong className="text-muted">Módulos sin cambios:</strong> {confirmModal.details.sinCambios}
                    </li>
                  )}
                  {confirmModal.details.cantidad && (
                    <li className="mb-2">
                      <strong>Cantidad de módulos:</strong> {confirmModal.details.cantidad}
                    </li>
                  )}
                  {confirmModal.details.modulos && confirmModal.details.modulos.length > 0 && (
                    <li className="mb-2">
                      <strong>Módulos seleccionados:</strong>
                      <ul className="mt-2 mb-0">
                        {confirmModal.details.modulos.map((modulo, idx) => (
                          <li key={idx} className="text-muted small">{modulo}</li>
                        ))}
                        {confirmModal.details.totalModulos > confirmModal.details.modulos.length && (
                          <li className="text-muted small">
                            <em>... y {confirmModal.details.totalModulos - confirmModal.details.modulos.length} más</em>
                          </li>
                        )}
                      </ul>
                    </li>
                  )}
                </ul>
              </div>
            )}

            <Alert variant={confirmModal.variant === 'danger' ? 'danger' : confirmModal.variant === 'warning' ? 'warning' : 'info'} className="mb-0">
              {confirmModal.variant === 'danger' && 'Esta acción no se puede deshacer.'}
              {confirmModal.variant === 'warning' && 'Por favor, verifique la información antes de continuar.'}
              {confirmModal.variant === 'info' && 'Se aplicará la configuración a los módulos seleccionados.'}
            </Alert>
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button 
              variant="secondary" 
              onClick={handleCloseConfirmModal}
              className="px-4"
            >
              <FaTimes className="me-2" />
              Cancelar
            </Button>
            <Button 
              variant={confirmModal.variant === 'danger' ? 'danger' : confirmModal.variant === 'warning' ? 'warning' : 'primary'}
              onClick={() => {
                if (confirmModal.onConfirm) {
                  confirmModal.onConfirm();
                }
              }}
              className="px-4"
            >
              {confirmModal.variant === 'danger' && <FaTrash className="me-2" />}
              {confirmModal.variant === 'warning' && <FaCheck className="me-2" />}
              {confirmModal.variant === 'info' && <FaCheck className="me-2" />}
              Confirmar
            </Button>
          </Modal.Footer>
        </Modal>

        <ToastContainer />
      </div>
    </>
  );
};

export default MantenimientoParametriaModulos;
